/**
 * Loading states.
 *
 * Two kinds, and the choice between them is not cosmetic:
 *
 * - A **skeleton** where the shape of what is coming is already known. It holds
 *   the layout still, so nothing jumps when the data lands, and it tells the
 *   reader what they are waiting for. `app.css` has shipped the `.sk` shimmer
 *   since the prototype and nothing in this build had used it.
 * - A **spinner** only where the shape is genuinely unknown, or where the wait
 *   is a whole-page one with nothing to outline yet.
 *
 * A spinner over a region whose layout is predictable is a worse version of a
 * skeleton: it collapses the container, so the page reflows on arrival, and it
 * says "wait" without saying for what.
 */
import { useEffect, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';

/* -------------------------------------------------------------------------- */
/* Spinners                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * An inline spinner.
 *
 * The ring is `.ldr` — a conic gradient masked to an annulus, in the brand ramp
 * — rather than Bootstrap's `.spinner-border`, whose flat two-tone border is
 * the one piece of un-branded chrome that used to appear on every screen in the
 * product while it loaded.
 *
 * @param {{label?:string, size?:number, className?:string}} props
 */
export function Spinner({ label = 'Loading', size = 22, className = '' }) {
  return (
    <div className={`ldr-inline ${className}`} role="status">
      <span className="ldr" style={{ '--ldr-size': `${size}px` }} aria-hidden="true" />
      {label && <span className="ldr-label">{label}…</span>}
    </div>
  );
}

/**
 * The spinner that goes inside a button while its action is in flight.
 *
 * Separate from `Spinner` for one reason: the ring's colour. `Spinner` paints a
 * fixed brand gradient, which is invisible on a brand-coloured button — so this
 * inherits `currentColor`, which is exactly the property that made Bootstrap's
 * `.spinner-border` the right choice inside a button and the wrong one on a page.
 *
 * @param {{size?:number, className?:string}} props
 */
export function ButtonSpinner({ size = 13, className = 'me-2' }) {
  return (
    <span
      className={`ldr ldr-btn ${className}`}
      style={{ '--ldr-size': `${size}px` }}
      aria-hidden="true"
    />
  );
}

/**
 * The whole-page wait: restoring a session, or a route with nothing to outline.
 *
 * Held back for 180ms. A resolve that finishes in 40ms — a warm cache, a fast
 * `/auth/me` — would otherwise flash a loader on and off, which reads as a
 * glitch rather than as progress. Below the threshold the user sees the previous
 * frame and then the content, with nothing in between.
 */
export function PageLoader({ label = 'Loading', delay = 180 }) {
  const shown = useDelay(delay);
  if (!shown) return null;

  return (
    <div className="page-loader" role="status">
      <span className="ldr" style={{ '--ldr-size': '38px' }} aria-hidden="true" />
      <div className="page-loader-label">{label}…</div>
    </div>
  );
}

/** True once `ms` has passed since mount. */
function useDelay(ms) {
  const [ready, setReady] = useState(ms === 0);
  useEffect(() => {
    if (ms === 0) return undefined;
    const t = setTimeout(() => setReady(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return ready;
}

/**
 * A hairline bar across the top of the page while any query is in flight.
 *
 * This is for the fetches that have no loader of their own: a background
 * refetch on window focus, a cursor page, an invalidation after a mutation.
 * Those repaint a screen that already has content, so they must not be allowed
 * to replace it with a skeleton — but with no indication at all, a list that
 * silently changes under the reader looks like a bug.
 *
 * Position-fixed and 2px tall, so it costs no layout.
 */
export function TopProgressBar() {
  const fetching = useIsFetching();
  /* Same reasoning as PageLoader: most queries resolve inside 200ms from cache,
     and a bar that strobes on every keystroke of a debounced search is noise. */
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!fetching) {
      setVisible(false);
      return undefined;
    }
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, [fetching]);

  if (!visible) return null;
  return <div className="nprog" role="progressbar" aria-label="Loading" aria-busy="true" />;
}

/* -------------------------------------------------------------------------- */
/* Skeletons                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * One shimmering block. `.sk` is the prototype's own gradient sweep.
 *
 * @param {{w?:string|number, h?:string|number, r?:string|number, className?:string}} props
 */
export function Skeleton({ w = '100%', h = 12, r, className = '', style }) {
  return (
    <span
      className={`sk ${className}`}
      style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }}
      aria-hidden="true"
    />
  );
}

/**
 * Lines of text.
 *
 * The last line is short on purpose: a paragraph of equal-length bars reads as
 * a table, and the ragged edge is what makes it read as prose.
 */
export function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={`d-flex flex-column gap-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} h={10} w={i === lines - 1 ? '62%' : '100%'} />
      ))}
    </div>
  );
}

/**
 * A table's own shape, in its own markup.
 *
 * A real `<table class="tbl">` with real `<th>`s, because that is what makes the
 * column widths and the row height identical to the loaded table — the point of
 * a skeleton is that nothing moves when the data arrives. A `<div>` grid
 * approximating a table moves.
 *
 * `columns` accepts the same array the live table renders, so a caller cannot
 * end up with a skeleton of a different width from the thing it stands in for.
 */
export function SkeletonTable({ columns, rows = 6 }) {
  const cols = Array.isArray(columns) && columns.length
    ? columns
    : Array.from({ length: 5 }, (_, i) => ({ key: i, header: '' }));

  /* Varied widths so the block does not read as a solid rectangle. Indexed
     rather than random: a skeleton that reshuffles on every re-render flickers. */
  const WIDTHS = ['78%', '54%', '66%', '42%', '70%', '48%', '60%'];

  return (
    <div className="tbl-wrap">
      <table className="tbl" aria-busy="true">
        <thead>
          <tr>
            {cols.map((c, i) => (
              <th key={c.key ?? i} style={c.width ? { width: c.width } : undefined}>
                {typeof c.header === 'string' ? c.header : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {cols.map((c, i) => (
                <td key={c.key ?? i}>
                  <Skeleton h={10} w={WIDTHS[(r + i) % WIDTHS.length]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The `.stat` tile band, with its icon square and two text lines. */
export function SkeletonKpiRow({ count = 4, cols = 'col-6 col-lg-3' }) {
  return (
    <div className="row g-3 mb-3" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div className={cols} key={i}>
          <div className="stat h-100">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <Skeleton w={34} h={34} r={11} />
              <Skeleton w={44} h={18} r={999} />
            </div>
            <Skeleton w="52%" h={26} />
            <Skeleton w="72%" h={10} className="mt-2" />
            <Skeleton w="86%" h={9} className="mt-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A chart's footprint: bars of settled heights inside the real `.card-body-x`
 * padding, so the card is exactly as tall as it will be.
 */
export function SkeletonChart({ height = 236 }) {
  const HEIGHTS = [44, 68, 52, 82, 60, 94, 72, 56, 88, 64, 78, 48];
  return (
    <div
      className="d-flex align-items-end gap-2"
      style={{ height, paddingTop: 8 }}
      aria-hidden="true"
    >
      {HEIGHTS.map((h, i) => (
        <Skeleton key={i} w="100%" h={`${h}%`} r={5} style={{ flex: 1 }} />
      ))}
    </div>
  );
}

/** A card shell: header line, then either a chart footprint or text lines. */
export function SkeletonCard({ height, lines = 3, className = '', head = true }) {
  return (
    <section className={`card-x ${className}`} aria-busy="true">
      {head && (
        <div className="card-head">
          <div className="flex-grow-1">
            <Skeleton w="34%" h={13} />
            <Skeleton w="52%" h={9} className="mt-2" />
          </div>
        </div>
      )}
      <div className="card-body-x">
        {height ? <SkeletonChart height={height} /> : <SkeletonText lines={lines} />}
      </div>
    </section>
  );
}

/**
 * A dashboard's own arrangement — hero band, KPI row, then a wide card beside a
 * narrow one.
 *
 * All four dashboards share this silhouette, so one skeleton covers them and
 * the switch to real content is a fill rather than a re-layout.
 */
export function SkeletonDashboard({ hero = true, kpiCols }) {
  return (
    <div aria-busy="true">
      <div className="page-head">
        <div>
          <Skeleton w={230} h={22} />
          <Skeleton w={320} h={11} className="mt-2" />
        </div>
      </div>
      {hero && <Skeleton h={132} r="var(--r-lg)" className="mb-3" />}
      <SkeletonKpiRow cols={kpiCols} />
      <div className="row g-3">
        <div className="col-xl-8"><SkeletonCard height={236} /></div>
        <div className="col-xl-4"><SkeletonCard height={236} /></div>
      </div>
    </div>
  );
}
